import {
  LeftOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
} from '@ant-design/icons';
import { Divider, Drawer, Flex, Menu, Typography } from 'antd';
import Button from 'components/Button';
import useRouteConfig from 'hooks/useRouteConfig';
import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { sidebarRoutes } from 'routes/AllRoutes';
import { getAppVersion } from 'utils/helpers';
import testIds from 'utils/testId';
import './mobile.css';
import './style.scss';

type RoutesKey = keyof typeof sidebarRoutes;

const MobileSidebarButton = ({
  className,
  isOpen,
  onClick,
}: {
  className?: string;
  isOpen: boolean;
  onClick: () => void;
}) => (
  <div className={`sidebar-mobile-button ${className}`} onClick={onClick}>
    <MenuUnfoldOutlined style={{ display: isOpen ? 'none' : 'block' }} />
    <MenuFoldOutlined style={{ display: isOpen ? 'block' : 'none' }} />
  </div>
);

const Sidebar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { pathname } = useLocation();

  const { menuItems } = useRouteConfig();

  const selectedRoute = useMemo(() => {
    const route = Object.keys(sidebarRoutes).find(
      (key) => sidebarRoutes[key as RoutesKey] === pathname,
    );
    // need to automate this later
    if (
      route === 'farmerGroupLeader' ||
      route === 'paddyBuyer' ||
      route === 'machineOwner'
    )
      return 'stakeholders';
    if (route === 'taskWeeklyView' || route === 'taskAgronomistView')
      return 'taskManagement';
    return route;
  }, [pathname]);

  const appVersion = (
    <Flex vertical className="app-version-container">
      <Divider
        className="mx-auto w-75 min-w-75"
        style={{ minWidth: '50%', margin: '16px 0' }}
      />
      <Typography.Text
        className="version-text"
        type="secondary"
        data-testid={testIds?.sidebar?.['text-sidebar-app_version']}
      >
        Version {getAppVersion()}
      </Typography.Text>
      <Button
        className="mobile-close-button"
        color="default"
        type="text"
        onClick={() => setIsOpen(!isOpen)}
        trackerLabel="Close Sidebar"
      >
        <LeftOutlined />
      </Button>
    </Flex>
  );

  const menuContent = (
    <div className="sidebar-menu-items">
      <Menu
        className="side-menu"
        // onClick={onClick}
        selectedKeys={selectedRoute ? [selectedRoute] : []}
        defaultOpenKeys={['mrv', 'collections', 'groupManagement']}
        mode="inline"
        items={menuItems}
        data-testid={testIds?.sidebar?.['sidebar-menu']}
      />
      {appVersion}
    </div>
  );

  return (
    <>
      <div className="sidebar">{menuContent}</div>
      {/* 
        Mobile sidebar floating button is using two identical button, one that's static on the side 
        of the screen, and one that sticks to the right side of the menu drawer.

        It's to create an illusion that the floating button will be dragged together with the drawer,
        since there's limitation with only using the Ant Design Drawer component 
        */}
      <MobileSidebarButton
        className={`button-layer-1 ${isOpen ? 'sidebar-button-open' : 'sidebar-button-close'}`}
        isOpen={isOpen}
        onClick={() => setIsOpen(!isOpen)}
      />
      <Drawer
        width={236}
        open={isOpen}
        onClose={() => setIsOpen(false)}
        closable={false}
        placement="left"
        styles={{ body: { padding: 0, maxHeight: '100vh' } }}
        rootClassName="sidebar-drawer"
      >
        <MobileSidebarButton
          className="button-layer-2"
          isOpen={isOpen}
          onClick={() => setIsOpen(!isOpen)}
        />
        {menuContent}
      </Drawer>
    </>
  );
};

export default Sidebar;
